import { supabase, DEFAULT_ORG_ID, applyOrgFilter, signImagesDeep } from '../supabaseClient'
import { getCurrentStaff } from './auth'

export async function getJuegosByTorneo(torneoId) {
  let query = supabase
    .from('juegos')
    .select(`
      *,
      grupo:grupos(id, nombre_del_grupo, categoria_base, rama),
      equipo:equipos(id, nombre, nombre_corto, logo_url, color_primario),
      torneo:torneos(id, nombre, fecha_inicio, fecha_fin, org_organizadora_id)
    `)
    .eq('torneo_id', torneoId)
    .eq('activo', true)
    .is('deleted_at', null)
    .order('fecha', { ascending: true })
    .order('hora', { ascending: true });

  query = applyOrgFilter(query);
  const { data, error } = await query;

  if (error) {
    console.error('[getJuegosByTorneo] Error:', error);
    throw error;
  }
  return data;
}

export async function getJuegosByGrupo(grupoId) {
  let query = supabase
    .from('juegos')
    .select(`
      *,
      grupo:grupos(id, nombre_del_grupo),
      torneo:torneos(id, nombre, liga_id, temporada_id, tipo_torneo, org_organizadora_id),
      equipo:equipos(id, nombre, logo_url, color_primario)
    `)
    .eq('grupo_id', grupoId)
    .eq('activo', true)
    .is('deleted_at', null)
    .order('fecha', { ascending: false });

  query = applyOrgFilter(query);
  const { data, error } = await query;

  if (error) {
    console.error('[getJuegosByGrupo] Error:', error);
    throw error;
  }
  return data;
}

// Los juegos de VARIOS grupos en una sola consulta. La pantalla de Juegos hacia una
// consulta por grupo (14 en un club real) y el desplegable de "Crear juego" esperaba a
// todas: el dueno lo sintio como "tarda mucho en cargar los torneos" (2026-09-07).
export async function getJuegosByGrupoIds(grupoIds = []) {
  const ids = Array.from(new Set((grupoIds || []).filter(Boolean)));
  if (ids.length === 0) return [];
  let query = supabase
    .from('juegos')
    .select(`
      *,
      grupo:grupos(id, nombre_del_grupo),
      torneo:torneos(id, nombre, liga_id, temporada_id, tipo_torneo, org_organizadora_id),
      equipo:equipos(id, nombre, logo_url, color_primario)
    `)
    .in('grupo_id', ids)
    .eq('activo', true)
    .is('deleted_at', null)
    .order('fecha', { ascending: false });

  query = applyOrgFilter(query);
  const { data, error } = await query;

  if (error) {
    console.error('[getJuegosByGrupoIds] Error:', error);
    throw error;
  }
  return data || [];
}

export async function getJuegoById(juegoId) {
  let query = supabase
    .from('juegos')
    .select(`
      *,
      grupo:grupos(id, nombre_del_grupo, categoria_base, rama),
      equipo:equipos(id, nombre, nombre_corto, logo_url),
      torneo:torneos(id, nombre, tipo_torneo, liga_id, temporada_id, org_organizadora_id, fecha_inicio, fecha_fin),
      created_by_staff:staff(id, nombre_completo)
    `)
    .eq('id', juegoId);

  query = applyOrgFilter(query);
  const { data, error } = await query.single();

  if (error) {
    console.error('[getJuegoById] Error:', error);
    throw error;
  }
  return data;
}

export async function getJuegosByEquipo(equipoId) {
  let query = supabase
    .from('juegos')
    .select(`
      id,
      nombre,
      fecha,
      hora,
      grupo_id,
      torneo_id,
      estatus,
      grupo:grupos(id, nombre_del_grupo),
      torneo:torneos(id, nombre)
    `)
    .eq('equipo_id', equipoId)
    .eq('activo', true)
    .is('deleted_at', null)
    .order('fecha', { ascending: false })
    .order('hora', { ascending: false });

  query = applyOrgFilter(query);
  const { data, error } = await query;

  if (error) {
    console.error('[getJuegosByEquipo] Error:', error);
    throw error;
  }

  return data || [];
}

export async function replaceEquipoInJuegos(oldEquipoId, newEquipoId) {
  const { data: newEquipo, error: newEquipoError } = await supabase
    .from('equipos')
    .select('id, nombre')
    .eq('id', newEquipoId)
    .single();

  if (newEquipoError) {
    console.error('[replaceEquipoInJuegos] Error loading new equipo:', newEquipoError);
    throw newEquipoError;
  }

  const { data, error } = await supabase
    .from('juegos')
    .update({
      equipo_id: newEquipo.id,
      equipo_nombre: newEquipo.nombre
    })
    .eq('equipo_id', oldEquipoId)
    .eq('activo', true)
    .is('deleted_at', null)
    .select('id');

  if (error) {
    console.error('[replaceEquipoInJuegos] Error:', error);
    throw error;
  }

  return data || [];
}

async function findJuegoConflict({ torneo_id, grupo_id, fecha, hora, excludeId = null }) {
  if (!torneo_id || !grupo_id || !fecha || !hora) return null;

  let query = supabase
    .from('juegos')
    .select('id, torneo_id, grupo_id, fecha, hora, estatus')
    .eq('torneo_id', torneo_id)
    .eq('grupo_id', grupo_id)
    .eq('fecha', fecha)
    .eq('hora', hora)
    .eq('activo', true)
    .is('deleted_at', null);

  if (excludeId) {
    query = query.neq('id', excludeId);
  }

  query = applyOrgFilter(query);
  const { data, error } = await query.limit(1).maybeSingle();

  if (error && error.code !== 'PGRST116') {
    throw error;
  }

  return data || null;
}

export async function createJuego(juegoData) {
  const conflicto = await findJuegoConflict(juegoData);
  if (conflicto) {
    throw new Error('Ya existe un juego de este grupo en la misma fecha y hora.');
  }

  const { data: staffData } = await getCurrentStaff();

  const payload = {
    ...juegoData,
    created_by: staffData?.id || null
  };

  const { data, error } = await supabase
    .from('juegos')
    .insert([payload])
    .select()
    .single();

  if (error) {
    if (error.code === '23505') {
      throw new Error('Ya existe un juego de este grupo en la misma fecha y hora.');
    }
    console.error('[createJuego] Error:', error);
    throw error;
  }
  return data;
}

export async function updateJuego(juegoId, juegoData) {
  const conflicto = await findJuegoConflict({ ...juegoData, excludeId: juegoId });
  if (conflicto) {
    throw new Error('Ya existe un juego de este grupo en la misma fecha y hora.');
  }

  const { data, error } = await supabase
    .from('juegos')
    .update(juegoData)
    .eq('id', juegoId)
    .select()
    .single();

  if (error) {
    if (error.code === '23505') {
      throw new Error('Ya existe un juego de este grupo en la misma fecha y hora.');
    }
    console.error('[updateJuego] Error:', error);
    throw error;
  }
  return data;
}

export async function reassignJuego(juegoId, nuevoGrupoId) {
  if (!juegoId || !nuevoGrupoId) {
    throw new Error('Juego y grupo destino son requeridos.');
  }

  const { data: staffData } = await getCurrentStaff();
  const { data, error } = await supabase.rpc('reasignar_juego', {
    p_juego_id: juegoId,
    p_nuevo_grupo_id: nuevoGrupoId,
    p_staff_id: staffData?.id || null
  });

  if (error) {
    console.error('[reassignJuego] Error:', error);
    throw error;
  }

  return data;
}

export async function registrarResultadoJuego(juegoId, resultado) {
  const payload = {
    puntos_favor: resultado.puntos_favor,
    puntos_contra: resultado.puntos_contra,
    comentarios: resultado.comentarios || null,
    notas_tecnicas: resultado.notas_tecnicas || null,
    estatus: 'terminado'
  };

  // Resultado (W/L/D) is calculated automatically by trigger

  const { data, error } = await supabase
    .from('juegos')
    .update(payload)
    .eq('id', juegoId)
    .select()
    .single();

  if (error) {
    console.error('[registrarResultadoJuego] Error:', error);
    throw error;
  }
  return data;
}

export async function deleteJuego(juegoId) {
  const { data, error } = await supabase
    .from('juegos')
    .update({
      activo: false,
      deleted_at: new Date().toISOString(),
      estatus: 'cancelado'
    })
    .eq('id', juegoId)
    .select()
    .single();

  if (error) {
    console.error('[deleteJuego] Error:', error);
    throw error;
  }
  return data;
}

// ========== ASISTENCIAS A JUEGOS ==========

export async function getAsistenciasByJuego(juegoId) {
  let query = supabase
    .from('asistencias')
    .select(`
      *,
      jugador:jugadores(id, nombre_completo, nickname, numero, imagen_url, imagen_path),
      staff:staff!asistencias_staff_id_fkey(id, nombre_completo)
    `)
    .eq('juego_id', juegoId)
    .eq('activa', true)
    .order('jugador(nombre_completo)');

  query = applyOrgFilter(query);
  const { data, error } = await query;

  if (error) {
    console.error('[getAsistenciasByJuego] Error:', error);
    throw error;
  }
  await signImagesDeep(data);
  return data;
}

export async function saveAsistenciaJuego(asistenciasArray) {
  try {
    const records = Array.isArray(asistenciasArray) ? asistenciasArray : [asistenciasArray];
    const results = [];

    for (const record of records) {
      if (!record.juego_id || !record.jugador_id) {
        throw new Error('Los registros de asistencia deben incluir juego_id y jugador_id');
      }

      let existingQuery = supabase
        .from('asistencias')
        .select('id')
        .eq('juego_id', record.juego_id)
        .eq('jugador_id', record.jugador_id)
        .eq('activa', true);
      existingQuery = applyOrgFilter(existingQuery);
      const { data: existing, error: selectError } = await existingQuery.maybeSingle();

      if (selectError && selectError.code !== 'PGRST116') {
        throw selectError;
      }

      if (existing?.id) {
        const { data: updated, error: updateError } = await supabase
          .from('asistencias')
          .update({
            presente: record.presente,
            descripcion: record.descripcion,
            fecha: record.fecha,
            staff_id: record.staff_id || null,
            grupo_id: record.grupo_id || null,
            sesion_id: record.sesion_id || null,
            activa: true
          })
          .eq('id', existing.id)
          .select()
          .single();

        if (updateError) throw updateError;
        results.push(updated);
      } else {
        const { data: inserted, error: insertError } = await supabase
          .from('asistencias')
          .insert({
            jugador_id: record.jugador_id,
            grupo_id: record.grupo_id,
            juego_id: record.juego_id,
            sesion_id: record.sesion_id || null,
            fecha: record.fecha,
            presente: record.presente,
            descripcion: record.descripcion,
            staff_id: record.staff_id || null,
            activa: true
          })
          .select()
          .single();

        if (insertError) throw insertError;
        results.push(inserted);
      }
    }

    return results;
  } catch (error) {
    console.error('[saveAsistenciaJuego] Error:', error);
    throw error;
  }
}

export const getAsistenciasCountByJuegoIds = async (juegoIds = []) => {
  if (!Array.isArray(juegoIds) || juegoIds.length === 0) return { data: {}, error: null };
  try {
    let query = supabase
      .from('asistencias')
      .select('juego_id')
      .in('juego_id', juegoIds)
      .eq('activa', true);
    query = applyOrgFilter(query);
    const { data, error } = await query;

    if (error) throw error;
    const counts = (data || []).reduce((acc, row) => {
      if (!row?.juego_id) return acc;
      acc[row.juego_id] = (acc[row.juego_id] || 0) + 1;
      return acc;
    }, {});
    return { data: counts, error: null };
  } catch (error) {
    return { data: {}, error };
  }
};
